/**
 * ERPs Drift Audit Policy (TV1-2146)
 *
 * Loads the drift audit panel: the Drift Episode time-series LEFT JOINed, on
 * read, onto the ERP Sync Mirror. It is a pure READ path — it never writes a
 * mirror row and never persists a derived gap (ADR-069 D5). `DriftAuditRow` is
 * assembled here for rendering only and is discarded on close.
 *
 * Registered ONCE, in `createERPsPolicies` (so it initialises at `ERPInfo`, the
 * routed page). The drift TAB registers nothing — that is the documented
 * `erpInfoTabs.tsx` contract — hence the activation gate below on
 * `erpDetail.mode.type === 'view' && erpDetail.activeTab === 'drift'`.
 *
 * Tenant scoping is INHERITED, not re-implemented: the search routes already
 * apply `FilterByAccountIdTransformer` + `FilterByScopeTransformer`, so this
 * policy filters only on the axes the operator chose.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import type { Dispatch } from '@commandDrivenStateEngine/core/types';
import { ClientLibraryType, ReadmodelChange } from '@bett3r-dev/teselly-client-library';
import { ErpDriftEpisodeReadmodelType } from '@bett3r-dev/teselly-domain';
import { toast } from '@components/common/Toast/Toast';
import { DRIFT_PAGE_SIZE, DriftAuditRow, DriftRecheckOutcome, DriftValue, ERPsState } from '../state';
import { ERPsCommand } from '../commands';

/**
 * Signal-mode subscriptions carry NO data and do NOT auto-refetch (known pv3
 * behaviour): a signal only says "something changed". Bursts are common while a
 * poll cycle opens/closes several episodes, so the refetch is debounced.
 */
const SIGNAL_DEBOUNCE_MS = 500;

type DispatchFn = Dispatch<ERPsCommand>;

type MirrorRow = {
  id: string;
  warehouseId?: string;
  priceListId?: string;
  externalProductId?: string;
  externalScopeId?: string;
  currencyIsoCode?: string;
  values?: Record<string, unknown>;
  observedAt?: string;
  writeDate?: string;
};

/** True only when the drift panel is the thing on screen. */
const isActive = ( state: ERPsState ): boolean =>
  state.erpDetail.mode.type === 'view' && state.erpDetail.activeTab === 'drift';

/**
 * Everything the load depends on, collapsed into one comparable string. A
 * change here (activation, deactivation, filter, page, or switching to another
 * ERP) is exactly the set of transitions that must re-run the load — and
 * nothing the policy itself dispatches alters it, so there is no feedback loop.
 */
const activationKey = ( state: ERPsState ): string | null => {
  if ( !isActive( state ) || state.erpDetail.mode.type !== 'view' ) return null;
  const { entity, status } = state.driftAudit.filter;
  return `${state.erpDetail.mode.erpId}|${entity}|${status}|${state.driftAudit.page}`;
};

const currentErpId = ( state: ERPsState ): string | null =>
  state.erpDetail.mode.type === 'view' ? state.erpDetail.mode.erpId : null;

/**
 * The episode's at-open values are typed as opaque objects by the generated
 * client (the readmodel stores them as free-form JSON). They are rendered, never
 * compared, so anything non-primitive degrades to its JSON text rather than to
 * `[object Object]`.
 */
const toDriftValue = ( value: unknown ): DriftValue | undefined => {
  if ( value === undefined ) return undefined;
  if ( value === null ) return null;
  if ( typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ) return value;
  return JSON.stringify( value );
};

export const createERPsDriftPolicy = (
  backend: ClientLibraryType
): Policy<ERPsState, ERPsCommand> => {
  let subscription: { close: () => void } | null = null;
  let signalTimer: ReturnType<typeof setTimeout> | null = null;
  let subscribedErpId: string | null = null;
  /** Monotonic guard so a slow load can never overwrite a newer one. */
  let loadToken = 0;

  const cancelSignalTimer = () => {
    if ( signalTimer ) {
      clearTimeout( signalTimer );
      signalTimer = null;
    }
  };

  const closeSubscription = () => {
    subscription?.close();
    subscription = null;
    subscribedErpId = null;
  };

  /**
   * Best-effort label resolution. The drift panel reads under
   * `sales-channels:read`; the inventory read is a DIFFERENT bag and the item
   * label is cosmetic, so a failure (including a 403 from a narrower operator)
   * must leave `itemLabel` undefined and let the table render the raw itemId.
   * No capability bag is widened for it.
   */
  const resolveItemLabels = async ( itemIds: string[]): Promise<Record<string, string>> => {
    if ( itemIds.length === 0 ) return {};
    try {
      const result = await backend.queries.ItemsPublishingInventorySearch({
        filter: [{ field: 'id', op: 'in', value: itemIds }],
        limit: itemIds.length
      });
      return ( result.pageItems ?? []).reduce<Record<string, string>>(( acc, item ) => {
        const label = item.sku || [ item.brand, item.model ].filter( Boolean ).join( ' ' );
        if ( label ) acc[item.id] = label;
        return acc;
      }, {});
    } catch {
      return {};
    }
  };

  const loadPage = async ( state: ERPsState, dispatch: DispatchFn ) => {
    const erpId = currentErpId( state );
    if ( !erpId ) return;

    const token = ++loadToken;
    const { entity, status } = state.driftAudit.filter;
    const page = state.driftAudit.page;

    dispatch(
      { type: 'integrations/erps/drift/load', payload: {}},
      { source: 'api' }
    );

    try {
      // `all` means "do not filter on this axis", so the clause is OMITTED —
      // sending `eq: 'all'` would match nothing.
      const episodesResult = await backend.queries.SystemsIntegrationsErpDriftEpisodesSearch({
        filter: [
          { field: 'integrationId', op: 'eq', value: erpId },
          ...( status === 'all' ? [] : [{ field: 'status', op: 'eq', value: status }]),
          ...( entity === 'all' ? [] : [{ field: 'entity', op: 'eq', value: entity }])
        ],
        limit: DRIFT_PAGE_SIZE,
        offset: ( page - 1 ) * DRIFT_PAGE_SIZE
      });
      if ( token !== loadToken ) return;

      const episodes = episodesResult.episodes ?? [];
      const syncKeys = Array.from( new Set( episodes.map(( episode ) => episode.syncKey )));

      // ONE mirror query per page, never one per row. And when the page yields
      // no syncKeys the query is SKIPPED entirely — `id in []` is a request that
      // can only return nothing, so issuing it is pure latency.
      let mirrorById: Record<string, MirrorRow> = {};
      if ( syncKeys.length > 0 ) {
        const mirrorResult = await backend.queries.SystemsIntegrationsErpSyncMirrorSearch({
          filter: [{ field: 'id', op: 'in', value: syncKeys }],
          limit: syncKeys.length
        });
        if ( token !== loadToken ) return;
        mirrorById = ( mirrorResult.rows ?? []).reduce<Record<string, MirrorRow>>(( acc, row ) => {
          acc[row.id] = row as MirrorRow;
          return acc;
        }, {});
      }

      const itemLabels = await resolveItemLabels(
        Array.from( new Set( episodes.map(( episode ) => episode.itemId ).filter(( id ): id is string => !!id )))
      );
      if ( token !== loadToken ) return;

      const rows: DriftAuditRow[] = episodes.map(( episode ) => {
        const mirror = mirrorById[episode.syncKey];
        return {
          episodeId: episode.id,
          syncKey: episode.syncKey,
          entity: episode.entity,
          field: episode.field,
          itemId: episode.itemId,
          itemLabel: episode.itemId ? itemLabels[episode.itemId] : undefined,
          // At-open snapshot — FROM THE EPISODE.
          ourValueAtOpen: toDriftValue( episode.ourValue ),
          theirValueAtOpen: toDriftValue( episode.theirValue ),
          // Last observed — FROM THE MIRROR. Deliberately a separate field: it
          // may already agree with us while the episode is still open.
          theirValueLastObserved: toDriftValue( mirror?.values?.[episode.field]),
          observedAt: mirror?.observedAt,
          writeDate: mirror?.writeDate,
          warehouseId: mirror?.warehouseId,
          priceListId: mirror?.priceListId,
          externalProductId: mirror?.externalProductId,
          externalScopeId: mirror?.externalScopeId,
          currencyIsoCode: mirror?.currencyIsoCode,
          openedAt: episode.openedAt,
          closedAt: episode.closedAt,
          durationMs: episode.durationMs,
          status: episode.status,
          // A join miss is reported EXPLICITLY. Scope and provenance live only
          // on the mirror row, and there is no syncKey parser (there is
          // deliberately none in `teselly-domain-utils`) — so the table renders
          // the raw syncKey rather than a synthesised scope label.
          mirrorMissing: !mirror
        };
      });

      dispatch(
        {
          type: 'integrations/erps/drift/dataLoaded',
          payload: { rows, total: episodesResult.count ?? rows.length }
        },
        { source: 'api' }
      );
    } catch ( error ) {
      if ( token !== loadToken ) return;
      // Clear resets the slice, loading included, so the panel never hangs on a
      // spinner. The error still surfaces through the global handler.
      dispatch(
        { type: 'integrations/erps/drift/clear', payload: {}},
        { source: 'api' }
      );
      throw error;
    }
  };

  const startSubscription = (
    erpId: string,
    getState: () => ERPsState,
    dispatch: DispatchFn
  ) => {
    if ( subscribedErpId === erpId && subscription ) return;
    closeSubscription();
    subscribedErpId = erpId;

    subscription = backend.subscriptions.SystemsIntegrationsErpDriftEpisodes(
      {
        // Signal mode: the server sends no rows, only a change notification. The
        // panel is paginated and joined, so a delta stream could not be applied
        // in place anyway — the signal is a trigger to re-run the page load.
        _snapshot: false,
        _changes: 'signal',
        filter: [{ field: 'integrationId', op: 'eq', value: erpId }]
      },
      ( change: ReadmodelChange<ErpDriftEpisodeReadmodelType> ) => {
        if ( change?.type !== 'signal' ) return;
        cancelSignalTimer();
        signalTimer = setTimeout(() => {
          signalTimer = null;
          const state = getState();
          // The tab may have changed while the debounce was pending.
          if ( isActive( state )) void loadPage( state, dispatch );
        }, SIGNAL_DEBOUNCE_MS );
      }
    );
  };

  /**
   * Per-row re-check (TV1-2146, decision D3). The operator asks Teselly to read
   * ONE syncKey out of the ERP right now instead of waiting for a poll that, for
   * a field the ERP never re-writes, by construction never comes.
   *
   * NOTHING FROM THE RESPONSE BECOMES ROW DATA (decision D6). The endpoint records
   * an observation server-side; the refreshed row arrives through the episodes
   * subscription, which is the single source of truth for what the row says. The
   * only thing dispatched back is the SETTLE — the row's spinner release plus the
   * verdict of the operator's own act, which is what makes a `not-found` visible on
   * the row and not only in a toast (SDD Risk R2).
   */
  const runRecheck = async ( syncKey: string, state: ERPsState, dispatch: DispatchFn ) => {
    // The i18n singleton is loaded LAZILY, inside the handler, and deliberately so.
    //
    // `translation/i18n.ts` runs `.use( initReactI18next ).init( ... )` at MODULE LOAD.
    // Importing it at the top of this file pulls that side effect into the import graph
    // of everything that reaches this policy — which is `ERPInfo` and therefore EVERY
    // ERP tab test. Six pre-existing suites (`ERPInfo`, `ERPItemsTab`, `ERPPriceListsTab`,
    // `ERPPricesTab`, `ERPStockTab`, `ERPWarehousesTab`) partially mock `react-i18next`
    // without `initReactI18next`, so a static import made them fail at COLLECTION time —
    // which vitest reports as failed FILES while still printing "all tests passed",
    // making it easy to miss entirely.
    //
    // Deferring the import to the moment an operator actually clicks re-check keeps the
    // side effect out of module graphs that never use it. Every `t()` below also passes a
    // literal fallback, so the toast degrades to Spanish rather than a raw key if the
    // singleton is somehow unavailable.
    const { default: i18n } = await import( '../../../../../translation/i18n' );
    const t = i18n.getFixedT( null, 'erps' );
    const erpId = currentErpId( state );
    let outcome: DriftRecheckOutcome = 'failed';

    try {
      if ( !erpId ) return;
      const result = await backend.post.erp_drift_recheck({ integrationId: erpId }, { syncKey });

      if ( result?.observed ) {
        outcome = 'observed';
        toast.success({
          title: t( 'erp_drift_recheck_observed_title', 'Re-consulta realizada' ),
          message: t(
            'erp_drift_recheck_observed_message',
            'Consultamos el ERP y registramos el valor actual. La fila se actualiza sola en unos segundos.'
          )
        });
      } else if ( result?.reason === 'not-found' ) {
        // DISTINCT on purpose: "no pasó nada" and "el producto ya no está en el
        // ERP" are opposite conclusions for the operator.
        outcome = 'not-found';
        toast.warning({
          title: t( 'erp_drift_recheck_not_found_title', 'El ERP ya no tiene este producto' ),
          message: t(
            'erp_drift_recheck_not_found_message',
            'El ERP no devolvió ningún dato para esta fila: el producto fue eliminado o ya no es visible para esta integración. La diferencia queda abierta.'
          )
        });
      } else if ( result?.reason === 'unsupported-entity' ) {
        outcome = 'unsupported-entity';
        toast.warning({
          title: t( 'erp_drift_recheck_unsupported_title', 'No se puede re-consultar esta fila' ),
          message: t(
            'erp_drift_recheck_unsupported_message',
            'El conector no permite consultar puntualmente este tipo de dato. La diferencia queda abierta.'
          )
        });
      } else {
        // `observed: false` with no reason: the server answered but concluded
        // nothing. Reported as a failure rather than silently as a success.
        toast.warning({
          title: t( 'erp_drift_recheck_failed_title', 'No pudimos re-consultar el ERP' ),
          message: t(
            'erp_drift_recheck_failed_message',
            'La consulta al ERP no se completó. La diferencia queda abierta; podés volver a intentarlo.'
          )
        });
      }
    } catch {
      // Swallowed on purpose: the settle below MUST run, and rethrowing from an
      // async policy call produces an unhandled rejection rather than reaching the
      // global handler. The operator gets the failure as a toast and on the row.
      toast.error({
        title: t( 'erp_drift_recheck_failed_title', 'No pudimos re-consultar el ERP' ),
        message: t(
          'erp_drift_recheck_failed_message',
          'La consulta al ERP no se completó. La diferencia queda abierta; podés volver a intentarlo.'
        )
      });
    } finally {
      // `finally`, never a success-path dispatch: a rejection must not strand the
      // row spinning forever.
      dispatch(
        {
          type: 'integrations/erps/drift/recheck/settled',
          payload: { syncKey, outcome }
        },
        { source: 'api' }
      );
    }
  };

  /** syncKeys whose re-check has already been started by THIS policy instance. */
  const inFlight = new Set<string>();

  const drainRecheckQueue = ( prev: ERPsState, next: ERPsState, dispatch: DispatchFn ) => {
    for ( const syncKey of next.driftAudit.rechecking ) {
      if ( prev.driftAudit.rechecking.includes( syncKey )) continue;
      if ( inFlight.has( syncKey )) continue;
      inFlight.add( syncKey );
      void runRecheck( syncKey, next, dispatch ).finally(() => inFlight.delete( syncKey ));
    }
  };

  const deactivate = ( dispatch: DispatchFn ) => {
    cancelSignalTimer();
    closeSubscription();
    // Invalidate any in-flight load so its response cannot repopulate the slice
    // after the clear.
    loadToken++;
    dispatch(
      { type: 'integrations/erps/drift/clear', payload: {}},
      { source: 'api' }
    );
  };

  const activate = ( state: ERPsState, getState: () => ERPsState, dispatch: DispatchFn ) => {
    const erpId = currentErpId( state );
    if ( !erpId ) return;
    startSubscription( erpId, getState, dispatch );
    void loadPage( state, dispatch );
  };

  return {
    name: 'erps-drift',

    /**
     * `init` covers the cold path: `ERPInfo` mounts with `?tab=drift` already
     * applied, so the activating transition happened BEFORE this subscription
     * attached and `onStateChange` would never see it.
     */
    init: ({ getState, dispatch }) => {
      const state = getState();
      if ( isActive( state )) activate( state, getState, dispatch );

      return () => {
        cancelSignalTimer();
        closeSubscription();
        loadToken++;
      };
    },

    /**
     * Two independent triggers, deliberately in ONE policy: the tab registry
     * forbids a tab from registering policies, so `ERPInfo` is the single
     * registration site and this is the one drift policy there is.
     *
     * The second trigger is a per-syncKey QUEUE DRAIN, not a structural diff of
     * the rows — `onStateChange` is source-blind, so intent must be marked in
     * state (here: the `rechecking` list a `user` dispatch appends to) rather
     * than inferred from a prev/next comparison.
     */
    shouldReact: ( prev, next ) =>
      activationKey( prev ) !== activationKey( next ) ||
      next.driftAudit.rechecking.some(( key ) => !prev.driftAudit.rechecking.includes( key )),

    onStateChange: ({ prev, next, ctx }) => {
      // Drained FIRST and unconditionally: a re-check started on the last visible
      // page must still settle even if the same dispatch deactivated the panel.
      drainRecheckQueue( prev, next, ctx.dispatch );

      if ( activationKey( prev ) === activationKey( next )) return;

      if ( !isActive( next )) {
        deactivate( ctx.dispatch );
        return;
      }
      activate( next, ctx.getState, ctx.dispatch );
    }
  };
};
